// src/app/features/profile/faq/components/FAQAccordion.tsx
import { useState } from "react";
import { ChevronDown, HelpCircle, Sparkles, Star} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { hapticFeedback } from "@tma.js/sdk";

const safeHaptic = (callback: () => void) => {
  try { callback(); } catch {}
};

export function FAQAccordion() {
  const { tRaw } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqItems = tRaw("faqItems");

  const toggleItem = (index: number) => {
    safeHaptic(() => hapticFeedback.selectionChanged());
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {faqItems.map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            className={`relative overflow-hidden rounded-2xl border backdrop-blur-sm ${
              isOpen
                ? "border-violet-400/50 dark:border-violet-300/30 shadow-lg shadow-violet-500/20 bg-gradient-to-br from-violet-200/70 via-white/80 to-fuchsia-200/70 dark:from-violet-900/40 dark:via-gray-900/60 dark:to-fuchsia-900/40"
                : "border-gray-200/60 dark:border-white/10 shadow-md shadow-black/5 bg-white/60 dark:bg-white/5 hover:border-violet-300/40 dark:hover:border-violet-400/20"
            }`}
          >
            {/* Декоративные элементы */}
            {isOpen && (
              <>
                <motion.span
                  className="absolute top-2 right-2 text-violet-400 dark:text-violet-300"
                  initial={{ opacity: 0, scale: 0, rotate: -20 }}
                  animate={{ opacity: 0.7, scale: 1, rotate: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Sparkles size={18} />
                </motion.span>
                <motion.span
                  className="absolute bottom-2 left-3 text-fuchsia-400 dark:text-fuchsia-300"
                  initial={{ opacity: 0, scale: 0, rotate: 20 }}
                  animate={{ opacity: 0.6, scale: 1, rotate: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <Star size={16} />
                </motion.span>
              </>
            )}

            <button
              onClick={() => toggleItem(index)}
              className="flex w-full items-center justify-between px-5 py-4 text-left relative z-10"
            >
              <span className="flex items-center gap-2 font-semibold text-foreground">
                <HelpCircle className={`h-4 w-4 shrink-0 ${isOpen ? 'text-violet-500' : 'text-muted-foreground'}`} />
                <span className="flex-1">{item.question}</span>
              </span>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0, scale: isOpen ? 1.1 : 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="ml-2 shrink-0"
              >
                <ChevronDown className={`h-5 w-5 ${isOpen ? 'text-violet-500' : 'text-muted-foreground'}`} />
              </motion.div>
            </button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                  className="overflow-hidden relative z-10"
                >
                  <div className="border-t border-violet-200/50 dark:border-white/10 px-5 pb-4 pt-3 text-sm leading-relaxed text-muted-foreground">
                    {item.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}